import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
  getDoc,
  arrayUnion,
} from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { firestore, storage, functions } from '../config/firebase';

const OFFER_VERSIONS = 'offerVersions';
const LISTING_OFFER_PREFIX = 'listing-offer-pdfs';

function offerVersionsCol(shareToken, interestId) {
  return collection(firestore, 'shares', shareToken, 'interests', interestId, OFFER_VERSIONS);
}

/**
 * @param {string} shareToken
 * @param {string} interestId
 * @param {(rows: Array<{ id: string } & object>) => void} callback
 */
export function subscribeOfferVersions(shareToken, interestId, callback) {
  if (!shareToken || !interestId) {
    callback([]);
    return () => {};
  }
  const q = query(offerVersionsCol(shareToken, interestId), orderBy('versionNumber', 'asc'));
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn('subscribeOfferVersions', err);
      callback([]);
    },
  );
}

async function nextVersionNumber(shareToken, interestId) {
  const snap = await getDocs(query(offerVersionsCol(shareToken, interestId), orderBy('versionNumber', 'desc'), limit(1)));
  if (snap.empty) return 1;
  const n = snap.docs[0].data()?.versionNumber;
  return typeof n === 'number' && !Number.isNaN(n) ? n + 1 : 1;
}

/**
 * @param {string} shareToken
 * @param {string} interestId
 * @param {string} recipientUid
 * @param {{
 *   price: string,
 *   conditions: string,
 *   delays: string,
 *   comments: string,
 *   buyerRepresentedByBroker: boolean,
 *   buyerBrokerEmail: string | null,
 * }} payload
 */
export async function submitIndicativeOffer(shareToken, interestId, recipientUid, payload) {
  if (!shareToken || !interestId || !recipientUid) throw new Error('shareToken, interestId, recipientUid required');
  const versionNumber = await nextVersionNumber(shareToken, interestId);
  const col = offerVersionsCol(shareToken, interestId);
  const docRef = await addDoc(col, {
    kind: 'indicative_buyer',
    versionNumber,
    status: 'submitted',
    createdAt: new Date(),
    createdByUid: recipientUid,
    price: String(payload.price ?? '').slice(0, 80),
    conditions: String(payload.conditions ?? '').slice(0, 8000),
    delays: String(payload.delays ?? '').slice(0, 2000),
    comments: String(payload.comments ?? '').slice(0, 8000),
    buyerRepresentedByBroker: !!payload.buyerRepresentedByBroker,
    buyerBrokerEmail: payload.buyerRepresentedByBroker
      ? String(payload.buyerBrokerEmail || '')
          .trim()
          .toLowerCase()
          .slice(0, 200)
      : null,
    artifacts: [],
  });
  const inviteEmail =
    payload.buyerRepresentedByBroker && payload.buyerBrokerEmail
      ? String(payload.buyerBrokerEmail).trim().toLowerCase().slice(0, 200)
      : null;
  if (inviteEmail) {
    try {
      await updateDoc(doc(firestore, 'shares', shareToken, 'interests', interestId), {
        pendingBuyerBrokerEmail: inviteEmail,
        pendingBuyerBrokerEmailAt: new Date(),
      });
    } catch (e) {
      console.warn('submitIndicativeOffer: interest buyer-broker fields', e);
    }
  }
  return docRef.id;
}

/**
 * Courtier vendeur : nouvelle version PDF officielle (contre-offre).
 */
export async function createFormalPdfVersion(shareToken, interestId, sellerUid, opts = {}) {
  if (!shareToken || !interestId || !sellerUid) throw new Error('missing args');
  const versionNumber = await nextVersionNumber(shareToken, interestId);
  const col = offerVersionsCol(shareToken, interestId);
  const docRef = await addDoc(col, {
    kind: 'formal_pdf',
    versionNumber,
    status: opts.status || 'submitted',
    createdAt: new Date(),
    createdByUid: sellerUid,
    supersedesVersionId: opts.supersedesVersionId || null,
    artifacts: [],
  });
  return docRef.id;
}

/**
 * Upload PDF and append to version.artifacts
 * @param {'selling_broker'|'buying_broker'|'buyer'} actorRole
 */
export async function appendOfferPdfArtifact(shareToken, interestId, versionDocId, file, uploaderUid, actorRole, documentLabel) {
  if (!shareToken || !interestId || !versionDocId || !file || !uploaderUid) throw new Error('missing args');
  const safeName = (file.name || 'offer.pdf').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${LISTING_OFFER_PREFIX}/${shareToken}/${interestId}/${versionDocId}/${Date.now()}_${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const uploadedAt = new Date().toISOString();
  const artifact = {
    storagePath: path,
    name: file.name || safeName,
    uploadedAt,
    uploadedByUid: uploaderUid,
    actorRole: actorRole || 'buyer',
    documentLabel: documentLabel || 'attachment',
  };
  const vref = doc(firestore, 'shares', shareToken, 'interests', interestId, OFFER_VERSIONS, versionDocId);
  const snap = await getDoc(vref);
  if (!snap.exists()) throw new Error('version not found');
  const patch = { artifacts: arrayUnion(artifact) };
  if (documentLabel === 'signed_return') {
    patch.buyerSignedUploadedAt = new Date();
  }
  await updateDoc(vref, patch);
  return artifact;
}

/**
 * Accepter une version formal_pdf (callable serveur : verrouillage, révocations groupées, audit).
 * @param {string} _sellerUid — conservé pour compatibilité appelants ; l’auth Firebase prime côté serveur.
 */
export async function acceptListingOfferVersion(shareToken, interestId, versionDocId, _sellerUid) {
  const acceptListingOffer = httpsCallable(functions, 'acceptListingOffer');
  const res = await acceptListingOffer({ shareToken, interestId, versionDocId });
  if (!res?.data?.ok) {
    throw new Error(typeof res?.data?.message === 'string' ? res.data.message : 'accept failed');
  }
}
